// SAB-493 Phase 3: minimal state + age verification form.
//
// Closes the loop for the 4c GeoBlockScreen `profile_incomplete` state: before
// this, the ONLY writer of user_responsible_gaming.{state_code, age_verified}
// was signup (Auth.tsx), so a user who reached the deposit/join eligibility
// gate without a verified RG row (legacy / MLB-beta / admin-created) had no way
// to fix it. This form calls the existing (previously-dead) `verify_age` edge
// action, which validates the state's fantasy-legality + minimum age server-side
// and upserts the RG row — the same authoritative path signup uses.
//
// Server stays authoritative: this form does NOT itself grant eligibility; it
// submits DOB + state and the edge function decides + persists.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ChevronLeft,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { STATE_ELIGIBILITY } from '@/lib/eligibility/config';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const US_STATES = Object.values(STATE_ELIGIBILITY)
  .map((s) => ({ code: s.code, name: s.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export default function VerifyEligibility() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // SAB-608: terminal underage block (age < the state minimum). Produced by the AGE gate,
  // rendered by the shared GeoBlockScreen's underage category.
  const [underageBlock, setUnderageBlock] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const submit = async () => {
    setError(null);
    if (!stateCode) {
      setError('Please select your state of residence.');
      return;
    }
    if (!dateOfBirth) {
      setError('Please enter your date of birth.');
      return;
    }
    setSubmitting(true);
    try {
      // SAB-500: set_eligibility (SECURITY DEFINER RPC, server-authoritative).
      // Derives the user from auth.uid(), computes age_verified server-side from
      // the DOB vs the state minimum, and upserts user_responsible_gaming past the
      // write-guard trigger. The client no longer sends age_verified.
      const { data, error: fnErr } = await supabase.rpc('set_eligibility', {
        p_date_of_birth: dateOfBirth,
        p_state_code: stateCode,
      });
      if (fnErr) throw new Error(fnErr.message ?? 'Verification failed.');
      const res = data as {
        age_verified?: boolean;
        eligible?: boolean;
        state_name?: string;
        minimum_age?: number;
      } | null;
      if (!res) {
        setError('Verification did not complete. Please try again.');
        return;
      }
      if (res.eligible === false) {
        setError(
          `Real-money fantasy sports contests are not currently available in ${res.state_name ?? 'your state'}.`,
        );
        return;
      }
      if (res.age_verified !== true) {
        // Server derived age < the state minimum: TERMINAL underage block (SAB-608).
        setUnderageBlock(true);
        return;
      }
      setDone(true);
      toast({ title: 'Verified', description: 'Your state and age are confirmed.' });
    } catch (e) {
      setError((e as Error).message ?? 'Verification failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Terminal underage: render the standardized block (no retry; support + RG line).
  if (underageBlock) {
    return <GeoBlockScreen reason={null} underage />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border">
        <CardHeader>
          <button
            onClick={() => navigate(-1)}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <div
            className={
              done
                ? 'mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/15'
                : 'mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15'
            }
          >
            {done ? (
              <CheckCircle2 className="h-7 w-7 text-green-500" />
            ) : (
              <ClipboardCheck className="h-7 w-7 text-primary" />
            )}
          </div>
          <CardTitle className="text-center text-2xl">
            {done ? "You're verified" : 'Verify your state & age'}
          </CardTitle>
          <CardDescription className="text-center">
            {done
              ? 'Your state and date of birth are confirmed. You can continue.'
              : 'Confirm the state you live in and your date of birth to play for real money. Required by law.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {done ? (
            <Button className="w-full" onClick={() => navigate(-1)}>
              Continue
            </Button>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="verify-state">State of residence</Label>
                <Select value={stateCode} onValueChange={setStateCode}>
                  <SelectTrigger id="verify-state">
                    <SelectValue placeholder="Select your state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="verify-dob">Date of birth</Label>
                <Input
                  id="verify-dob"
                  type="date"
                  max={today}
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </div>
              {error && (
                <p className="flex items-start gap-2 text-sm text-damage">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </p>
              )}
              <Button className="w-full" onClick={submit} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground/60">
                Must be of legal age and physically located in an eligible state to play paid
                contests.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
